import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Skeleton,
    Tooltip,
    Typography,
} from "@mui/material";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";

import { useNavigate, useParams } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";

import TourLayoutContainer from "../../../components/vanliferComponents/TourContainer";
import TourSidebar from "../../../components/vanliferComponents/TourSidebar";

import {
    asyncCancelTour,
    asyncModifyTourCommunity,
    asyncPostBookTour,
    loadTourDetail,
    loadTourDetailEnsureReserved
} from "../../../store/Vanlifer/tourSlice";

import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";

import TourPDFDocument from "../../../components/vanliferComponents/TourPDFDocument";
import TourItinerary from "./TourItinerary";

import { useSnackbar } from "../../../components/SnackbarProvider";
import { TourDetail } from "../../../services/restserver-openapi";
import EditTourDetailsDialog from "../../../components/vanliferComponents/Community/Dialogs/EditTourDetailsDialog";
import EditSharingLinkDialog from "../../../components/vanliferComponents/Community/Dialogs/EditSharingLinkDialog";
import sharingMode = TourDetail.sharingMode;
import StoriesView from "../../../components/vanliferComponents/Community/StoriesView/StoriesView";
import TourWeatherTab from "../../../components/vanliferComponents/TourWeatherTab";

import { urlAGBPDF } from "../../../constants/constants";
import { postPersonPreferencesAsync } from "../../../store/navBar/userSlice";

function TourPageSkeleton() {
    return (
        <TourLayoutContainer
            Sidebar={
                <TourSidebar>
                    <Box sx={{ p: 2 }}>
                        <Skeleton variant="text" width="70%" height={30} />
                        <Skeleton variant="text" width="90%" height={20} />
                        <Divider sx={{ my: 2 }} />
                        <Skeleton variant="text" width="60%" height={20} />
                        <Skeleton variant="text" width="80%" height={20} />
                        <Skeleton variant="text" width="55%" height={20} />
                        <Divider sx={{ my: 2 }} />
                        <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 2, mb: 1 }} />
                        <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 2, mb: 1 }} />
                        <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 2, mb: 1 }} />
                        <Divider sx={{ my: 2 }} />
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 2 }} />
                    </Box>
                </TourSidebar>
            }
            tabs={[
                {
                    label: "Tourverlauf",
                    content: (
                        <Box sx={{ maxWidth: 800, mx: "auto", p: 2 }}>
                            <Skeleton variant="text" width="35%" height={28} />
                            <Skeleton variant="text" width="60%" height={20} sx={{ mb: 2 }} />
                            {[0, 1, 2].map((i) => (
                                <Box key={i} sx={{ mb: 2 }}>
                                    <Skeleton variant="rectangular" height={52} sx={{ borderRadius: 2, mb: 1 }} />
                                    <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
                                </Box>
                            ))}
                        </Box>
                    ),
                },
                {
                    label: "Übersicht",
                    content: (
                        <Box sx={{ maxWidth: 800, mx: "auto", p: 2 }}>
                            <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
                        </Box>
                    ),
                },
            ]}
            floatingComponents={<></>}
        />
    );
}

const BOOKING_WINDOW_SECONDS = 10 * 60;

function formatMMSS(totalSeconds: number) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function TourPage() {
    const { showSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { tourId } = useParams();

    const loading = useAppSelector((s) => s.tour.loading);
    const chosenTourDetail = useAppSelector((s) => s.tour.choosedTourDetail);
    const detailedTourDistances = useAppSelector((state) => state.tour.detailedTourDistances);
    const userEntitlements = useAppSelector((s) => s.user.entitlements);

    const preferencesFromStore = useAppSelector((s) => s.user.preferences);
    const userId = useAppSelector((s) => s.login.userId);

    const safeBack = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate("/Vanlifer/TravelPlanning");
    };

    const activeTourId = useMemo(() => {
        if (!tourId) return null;
        const id = Number(tourId);
        return Number.isFinite(id) ? id : null;
    }, [tourId]);

    const [confirmDialogCancelOpen, setConfirmDialogCancelOpen] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [editLinkOpen, setEditLinkOpen] = useState(false);

    // Booking modal + timer
    const [confirmBookOpen, setConfirmBookOpen] = useState(false);
    const [agbAccepted, setAgbAccepted] = useState<boolean>(!!preferencesFromStore?.acceptedAgb);
    const [bookingSubmitting, setBookingSubmitting] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState<number>(BOOKING_WINDOW_SECONDS);
    const timedOutRef = useRef(false);

    useEffect(() => {
        setAgbAccepted(!!preferencesFromStore?.acceptedAgb);
    }, [preferencesFromStore?.acceptedAgb]);

    // Entitlements
    const canUseEditor = useMemo(
        () => userEntitlements.some((e) => e.featureId === "vanliferuseeditor" && e.value === "true"),
        [userEntitlements]
    );
    const canCancelTour = useMemo(
        () => userEntitlements.some((e) => e.featureId === "vanlifercanceltour" && e.value === "true"),
        [userEntitlements]
    );
    const canExportToPDF = useMemo(
        () => userEntitlements.some((e) => e.featureId === "vanliferpdfexport" && e.value === "true"),
        [userEntitlements]
    );
    const canShareTour = useMemo(
        () => userEntitlements.some((e) => e.featureId === "vanliferpublishcontent" && e.value === "true"),
        [userEntitlements]
    );
    const canBookTour = useMemo(
        () => userEntitlements.some((e) => e.featureId === "vanliferbooktour" && e.value === "true"),
        [userEntitlements]
    );

    // Load Tour Detail (Route basiert)
    useEffect(() => {
        const fetchTourDetail = async () => {
            if (!activeTourId) {
                showSnackbar("Ungültige Tour-ID", "error");
                safeBack();
                return;
            }
            try {
                await dispatch(loadTourDetailEnsureReserved({ tourId: activeTourId, accessKey: undefined })).unwrap();
            } catch (e) {
                showSnackbar("Fehler beim Laden der Tour", "error");
                safeBack();
            }
        };

        fetchTourDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, activeTourId]);


    const isCanceled = chosenTourDetail?.status === "CANCELED";
    const isBooked = chosenTourDetail?.status === "BOOKED";

    // robust: nur rendern, wenn wirklich die richtige Tour im Store ist
    const tourMatchesRoute = !!activeTourId && chosenTourDetail?.tourId === activeTourId;

    const bookingRequired = tourMatchesRoute && !isBooked && !isCanceled;

    // Save countdown (10 Minuten) solange nicht gespeichert
    useEffect(() => {
        if (!bookingRequired) {
            timedOutRef.current = false;
            return;
        }

        timedOutRef.current = false;
        setSecondsLeft(BOOKING_WINDOW_SECONDS);

        const interval = window.setInterval(() => {
            setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => window.clearInterval(interval);
    }, [bookingRequired, activeTourId]);

    useEffect(() => {
        if (!bookingRequired) return;
        if (secondsLeft !== 0) return;
        if (timedOutRef.current) return;

        timedOutRef.current = true;
        showSnackbar("Planungszeit abgelaufen – zurück zur vorherigen Seite", "info");
        safeBack();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondsLeft, bookingRequired]);

    // Robust: earliest fromDate
    const earliestFromDate = useMemo(() => {
        const stays = chosenTourDetail?.stays ?? [];
        if (stays.length === 0) return null;
        const earliest = stays.reduce((acc, s) => (s.fromDate! < acc.fromDate! ? s : acc));
        return earliest.fromDate ? new Date(earliest.fromDate) : null;
    }, [chosenTourDetail]);

    const tourDatePassed = useMemo(() => {
        if (!earliestFromDate) return false;
        return Date.now() > earliestFromDate.getTime();
    }, [earliestFromDate]);

    const within24HoursOfTourStart = useMemo(() => {
        if (!earliestFromDate) return false;
        const now = Date.now();
        const start = earliestFromDate.getTime();
        return now < start && start - now <= 24 * 60 * 60 * 1000;
    }, [earliestFromDate]);

    const { numberOfDays } = useMemo(() => {
        const stays = chosenTourDetail?.stays;
        if (!stays?.length) {
            return { numberOfDays: null as number | null };
        }

        const oldestStay = stays.reduce((oldest, stay) => (oldest.fromDate! < stay.fromDate! ? oldest : stay));
        const newestStay = stays.reduce((newest, stay) => (newest.toDate! > stay.toDate! ? newest : stay));

        const oldestFromDate = oldestStay.fromDate;
        const newestToDate = newestStay.toDate;

        let days: number | null = null;
        if (oldestFromDate && newestToDate) {
            const from = new Date(oldestFromDate);
            const to = new Date(newestToDate);
            const diffTime = to.getTime() - from.getTime();
            days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        }

        return { numberOfDays: days };
    }, [chosenTourDetail?.stays]);

    const stopCount = useMemo(() => {
        return (chosenTourDetail?.stays ?? []).filter((stay) => stay.stayType !== 'CUSTOM_START' && stay.stayType !== 'CUSTOM_END').length;
    }, [chosenTourDetail?.stays]);

    const itineraryEditingAllowed = !tourDatePassed && !within24HoursOfTourStart && !isCanceled;
    const itineraryEditingDisabledTooltip = isCanceled
        ? "Diese Tour wurde gelöscht"
        : within24HoursOfTourStart
            ? "Bearbeiten ist innerhalb von 24h vor Start nicht möglich"
            : tourDatePassed
                ? "Eine bereits begonnene Tour kann nicht bearbeitet werden"
                : undefined;

    // Buttons disabled
    const buttonCancelTourDisabled = tourDatePassed || within24HoursOfTourStart || !canCancelTour || isCanceled;
    const buttonPDFExportDisabled = !canExportToPDF;
    const buttonShareDiskabled = !canShareTour;

    const tooltipCancelTourText = useMemo(() => {
        if (isCanceled) return "Diese Tour wurde bereits gelöscht";
        if (within24HoursOfTourStart) return "Eine bald beginnende Tour kann nicht gelöscht werden";
        if (tourDatePassed) return "Eine vergangene/bereits angefangene Tour kann nicht gelöscht werden";
        if (!canCancelTour) return "Sie benötigen ein Abonnement um Touren zu löschen";
        return "";
    }, [isCanceled, within24HoursOfTourStart, tourDatePassed, canCancelTour]);

    const dateLabel = chosenTourDetail?.statusDate
        ? new Date(chosenTourDetail.statusDate).toLocaleDateString("de-CH")
        : "Keine Information";

    const priceLabel = chosenTourDetail?.price != null ? `${chosenTourDetail.price.toFixed(2)} CHF` : "Keine Information";
    const [highlightedStayId, setHighlightedStayId] = useState<number | null>(null);
    const [hoveredStayId, setHoveredStayId] = useState<number | null>(null);

    const handleOpenPDF = async () => {
        if (!chosenTourDetail) return;
        try {
            const blob = await pdf(<TourPDFDocument tour={chosenTourDetail} />).toBlob();
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, "_blank", "noopener,noreferrer");
        } catch (e) {
            showSnackbar("Fehler beim Erstellen des PDFs", "error");
        }
        showSnackbar("Reise erfolgreich als PDF exportiert, bitte überprüfe ob neue neue Tabs (Popups) erlaubt sind.", "success");
    };

    async function cancelTour() {
        setConfirmDialogCancelOpen(false);
        if (!tourId) return;

        try {
            await dispatch(asyncCancelTour({ tourId: Number(tourId) })).unwrap();
            navigate("/vanlifer/BookingOverview");
            showSnackbar("Reise erfolgreich gelöscht", "success");
        } catch (err: any) {
            console.error("cancelTour failed:", err);

            const msg =
                err?.body?.message ||
                err?.message ||
                "Fehler beim Löschen der Tour";

            showSnackbar(msg, "error");
            navigate("/vanlifer/BookingOverview");
        }
    }


    const handleEditSave = async (data: {
        name: string;
        description: string;
        sharingMode: TourDetail.sharingMode;
        hasNewImage: boolean;
        imageName?: string;
        fileExtension?: string;
    }) => {
        if (!activeTourId || !chosenTourDetail) return;

        const updatedTour = {
            tourId: chosenTourDetail.tourId!,
            name: data.name,
            description: data.description,
            sharingMode: data.sharingMode,
            ...(data.hasNewImage && data.imageName !== undefined
                ? { imageName: data.imageName, fileExtension: data.fileExtension }
                : data.hasNewImage
                    ? { imageName: "delete", fileExtension: "" }
                    : {}),
        };

        try {
            await dispatch(asyncModifyTourCommunity(updatedTour)).unwrap();
            await dispatch(loadTourDetail({ tourId: updatedTour.tourId, accessKey: undefined })).unwrap();
            showSnackbar("Reise erfolgreich aktualisiert", "success");
            setEditOpen(false);
        } catch {
            showSnackbar("Fehler beim Speichern der Reise", "error");
        }
    };

    const persistAgbPreferenceIfNeeded = async () => {
        try {
            if (!userId || !preferencesFromStore) return;
            if (agbAccepted && preferencesFromStore.acceptedAgb !== true) {
                await dispatch(
                    postPersonPreferencesAsync({
                        userId,
                        preferences: { ...preferencesFromStore, acceptedAgb: true },
                    })
                ).unwrap();
            }
        } catch {
            // nicht kritisch -> trotzdem weiter speichern
        }
    };

    const bookTour = async () => {
        if (!chosenTourDetail?.tourId) return;

        setBookingSubmitting(true);
        try {
            await persistAgbPreferenceIfNeeded();
            await dispatch(asyncPostBookTour({ tourId: chosenTourDetail.tourId })).unwrap();
            await dispatch(loadTourDetail({ tourId: chosenTourDetail.tourId, accessKey: undefined })).unwrap();
            showSnackbar("Tour erfolgreich gespeichert", "success");
            setConfirmBookOpen(false);
        } catch (error: any) {
            const message = error?.body?.message || error?.message || "";
            if (String(message).includes("Max amount reached")) {
                showSnackbar("Du hast das Limit für geplante Reisen erreicht. Bitte lösche zuerst eine zukünftige Reise.", "info");
            } else {
                showSnackbar("Fehler beim Speichern der Tour", "error");
            }
        } finally {
            setBookingSubmitting(false);
        }
    };

    const ActionRow = (props: {
        icon: React.ReactNode;
        title: React.ReactNode;
        subtitle?: React.ReactNode;
        onClick?: () => void;
        disabled?: boolean;
        tooltip?: string;
        tone?: "default" | "danger" | "secondary";
        variant?: "default" | "primaryOnSecondary";
        endAdornment?: React.ReactNode;
    }) => {
        const {
            icon,
            title,
            subtitle,
            onClick,
            disabled,
            tooltip,
            tone = "default",
            variant = "default",
            endAdornment,
        } = props;

        const isPrimaryOnSecondary = variant === "primaryOnSecondary";

        const iconColor = isPrimaryOnSecondary
            ? "primary.main"
            : tone === "danger"
                ? "error.main"
                : tone === "secondary"
                    ? "secondary.main"
                    : "text.secondary";

        const titleColor = isPrimaryOnSecondary
            ? "primary.main"
            : tone === "danger"
                ? "error.main"
                : tone === "secondary"
                    ? "secondary.main"
                    : "text.primary";

        const bgColor = isPrimaryOnSecondary ? "secondary.light" : "transparent";

        const content = (
            <ListItem disablePadding>
                <ListItemButton
                    disabled={disabled}
                    onClick={onClick}
                    sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        bgcolor: bgColor,
                        "&:hover": {
                            bgcolor: isPrimaryOnSecondary ? "secondary.light" : undefined,
                        },
                        "&.Mui-disabled": { opacity: 0.55 },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: iconColor }}>{icon}</ListItemIcon>

                    <ListItemText
                        primary={
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                <Box sx={{ fontWeight: 700, color: titleColor }}>{title}</Box>
                                {endAdornment}
                            </Box>
                        }
                        secondary={subtitle}
                        secondaryTypographyProps={{
                            // subtitle default bleibt text.secondary, außer du überschreibst in subtitle selbst
                            color: isPrimaryOnSecondary ? "text.secondary" : "text.secondary",
                        }}
                    />
                </ListItemButton>
            </ListItem>
        );

        return tooltip ? (
            <Tooltip title={tooltip}>
                <Box component="span">{content}</Box>
            </Tooltip>
        ) : (
            content
        );
    };

    // robust render guards
    if (!activeTourId) return <TourPageSkeleton />;
    if (loading || !tourMatchesRoute || !chosenTourDetail) return <TourPageSkeleton />;

    const bookingTooltip = !canBookTour ? "Du benötigst ein Abonnement, um Tourplanungen zu speichern" : "Tourplanung speichern (10 Minuten Zeitfenster)";

    return (
        <>
            <TourLayoutContainer
                Sidebar={
                    <TourSidebar highlightedStayId={highlightedStayId} hoveredStayId={hoveredStayId} onHoveredStayChange={setHoveredStayId}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Tourname
                            </Typography>

                            <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: "bold", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                    title={chosenTourDetail.name}
                                >
                                    {chosenTourDetail.name}
                                </Typography>
                            </Box>

                            {/* Description */}
                            <Typography component="div" variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                    {chosenTourDetail.description}
                                </Typography>
                            </Typography>

                            {/* Statistics */}
                            <Box sx={{ display: "flex", alignItems: "stretch", gap: 0 }}>
                                {[0, 1, 2, 3].map((index) => (
                                    <React.Fragment key={index}>
                                        <Box sx={{ flex: 1, px: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                                {[
                                                    stopCount,
                                                    numberOfDays,
                                                    Math.round(detailedTourDistances?.reduce((acc, val) => acc + val, 0) || 0),
                                                    chosenTourDetail.likeCount,
                                                ][index] as any}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {["Stopps", "Tage", "km", "Likes"][index]}
                                            </Typography>
                                        </Box>
                                        {index < 3 && <Divider orientation="vertical" flexItem />}
                                    </React.Fragment>
                                ))}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Booking Infos */}
                            <Typography variant="body2" color="text.secondary">
                                Geplant am
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                {dateLabel} von {chosenTourDetail.owner ?? "Unbekannter User"}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Geplante Reisekosten
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                {priceLabel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                (Vor Ort bei den jeweiligen Plätzen zu bezahlen)
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <List sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                {/* Solange nicht gespeichert: "Tourplanung speichern" (secondary) + Countdown */}
                                {bookingRequired && (
                                    <ActionRow
                                        icon={<EventAvailableRoundedIcon />}
                                        title="Tourplanung speichern"
                                        subtitle={
                                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                <Typography variant="body2" >
                                                    Verfügbar für {formatMMSS(secondsLeft)}
                                                </Typography>
                                                <Typography variant="body2" >
                                                    Bitte innerhalb von 10 Minuten bestätigen
                                                </Typography>
                                            </Box>
                                        }
                                        onClick={() => setConfirmBookOpen(true)}
                                        disabled={!canBookTour || bookingSubmitting}
                                        tooltip={bookingTooltip}
                                        tone="secondary"
                                        variant="primaryOnSecondary"
                                        endAdornment={
                                            <Typography variant="caption" sx={{ fontWeight: 800, color: "secondary.main" }}>
                                                {formatMMSS(secondsLeft)}
                                            </Typography>
                                        }
                                    />
                                )}

                                <ActionRow
                                    icon={<EditRoundedIcon />}
                                    title="Tourdetails bearbeiten"
                                    subtitle="Ändere den Namen und füge Details hinzu"
                                    onClick={() => setEditOpen(true)}
                                    tooltip={!canUseEditor ? "Fehlende Berechtigung (vanliferuseeditor)" : isCanceled ? "Eine gelöschte Tour kann nicht bearbeitet werden" : ""}
                                    disabled={!canUseEditor || isCanceled}
                                />

                                <ActionRow
                                    icon={<ShareRoundedIcon />}
                                    title="Tour mit Freunden teilen"
                                    subtitle={
                                        buttonShareDiskabled
                                            ? "Du benötigst ein Abonnement für das Teilen einer Tour"
                                            : "Teile Deine Tour mit Freunden und Familie"
                                    }
                                    onClick={() => setEditLinkOpen(true)}
                                    disabled={buttonShareDiskabled}
                                    tooltip={
                                        buttonShareDiskabled
                                            ? "Du benötigst ein Abonnement für das Teilen einer Tour"
                                            : "Teile Deine Tour mit Freunden und Familie"
                                    }
                                />

                                <ActionRow
                                    icon={<PictureAsPdfRoundedIcon />}
                                    title="Tour als PDF öffnen"
                                    subtitle={buttonPDFExportDisabled ? "Du benötigst ein Abonnement für den PDF Export" : "Öffnet das PDF in einem neuen Tab"}
                                    onClick={handleOpenPDF}
                                    disabled={buttonPDFExportDisabled}
                                    tooltip={buttonPDFExportDisabled ? "Sie benötigen ein Abonnement um die Tour als PDF zu exportieren" : "Tour als PDF im neuen Tab öffnen"}
                                />

                                {/* Nach Speichern: "Tour löschen" */}
                                {isBooked && (
                                    <ActionRow
                                        icon={<CancelRoundedIcon />}
                                        title="Tour löschen"
                                        subtitle={tooltipCancelTourText || "Lösche die Tour"}
                                        onClick={() => setConfirmDialogCancelOpen(true)}
                                        disabled={buttonCancelTourDisabled}
                                        tooltip={tooltipCancelTourText}
                                        tone="danger"
                                    />
                                )}
                            </List>

                            <Divider sx={{ my: 2 }} />

                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<ArrowBackIosNewRoundedIcon />}
                                onClick={safeBack}
                                fullWidth
                            >
                                Zurück
                            </Button>
                        </Box>
                    </TourSidebar>
                }
                tabs={[
                    {
                        label: "Tourverlauf",
                        content: (
                            <TourItinerary
                                defaultEditingEnabled={false}
                                showEditingToggle={true}
                                editingAllowed={itineraryEditingAllowed}
                                editingDisabledTooltip={itineraryEditingDisabledTooltip}
                                onHighlightedStayChange={setHighlightedStayId}
                                onHoveredStayChange={setHoveredStayId}
                                hoveredStayId={hoveredStayId}
                            />
                        ),
                    },
                    {
                        label: "Stories",
                        content: (
                            <StoriesView tourId={chosenTourDetail.tourId!} chosenTourDetail={chosenTourDetail} showSnackbar={showSnackbar} />
                        ),
                    },
                    {
                        label: "Wetter",
                        content: <TourWeatherTab stays={chosenTourDetail.stays} />,
                    },
                ]}
                floatingComponents={
                    <>
                        {/* Confirm Cancel */}
                        <Dialog open={confirmDialogCancelOpen} onClose={() => setConfirmDialogCancelOpen(false)}>
                            <DialogTitle>Tour löschen</DialogTitle>
                            <DialogContent>
                            <DialogContentText>Sind Sie sicher, dass Sie diese Tour löschen möchten?</DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setConfirmDialogCancelOpen(false)} color="primary">
                                    Nein
                                </Button>
                                <Button onClick={cancelTour} color="primary" autoFocus>
                                    Ja
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* Confirm Book + AGB */}
                        <Dialog open={confirmBookOpen} onClose={() => setConfirmBookOpen(false)} fullWidth maxWidth="sm">
                            <DialogTitle>Tourplanung speichern</DialogTitle>
                            <DialogContent>
                                <DialogContentText sx={{ mb: 2 }}>
                                    Bitte bestätigen Sie, dass Sie die AGB gelesen haben. Danach wird Ihre Tourplanung gespeichert.
                                </DialogContentText>

                                <FormControl>
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={agbAccepted} onChange={(e) => setAgbAccepted(e.target.checked)} required />
                                        }
                                        label={
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                                                <Typography variant="body2">Ich habe die</Typography>
                                                <Link href={urlAGBPDF} target="_blank" rel="noopener noreferrer" underline="hover">
                                                    AGB
                                                </Link>
                                                <Typography variant="body2">gelesen und akzeptiere sie.</Typography>
                                            </Box>
                                        }
                                    />
                                </FormControl>

                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Zeitfenster: {formatMMSS(secondsLeft)}
                                    </Typography>
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setConfirmBookOpen(false)} disabled={bookingSubmitting}>
                                    Abbrechen
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={bookTour}
                                    disabled={!agbAccepted || bookingSubmitting || !canBookTour}
                                >
                                    Tourplanung speichern
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <EditTourDetailsDialog
                            open={editOpen}
                            onClose={() => setEditOpen(false)}
                            onSave={handleEditSave}
                            tourId={chosenTourDetail.tourId}
                            initialTitle={chosenTourDetail.name}
                            initialDescription={chosenTourDetail.description}
                            initialSharingMode={chosenTourDetail.sharingMode}
                            initialSasLink={chosenTourDetail.sasDownloadLink}
                        />

                        <EditSharingLinkDialog
                            open={editLinkOpen}
                            tourId={chosenTourDetail.tourId!}
                            isPrivate={chosenTourDetail.sharingMode === sharingMode.PRIVATE}
                            onClose={() => setEditLinkOpen(false)}
                            showSnackbar={showSnackbar}
                        />
                    </>
                }
            />
        </>
    );
}
